// Landscape store
// --------------------------------------------------------
import Vue from 'vue'
import {
  Alert,
  AlertMessages,
  Endpoints,
  AccessControl,
  Roles,
  FeedSources,
  Events,
  RestrictedFeedSources
} from '@/lib/definitions'
import api from '@/lib/api'
import router from '@/router'
import EventHub, {newAlert} from '@/lib/eventHub'
import {GA_EVENTS, gaComposeEvent} from '@/lib/ga'
import utils from '@/store/modules/landscape/utils'
import preferences from '@/store/modules/landscape/preferences'
import tags from '@/store/modules/landscape/tags'

const state = {
  rootActorLimit: 7,
  landscape: null,
  actors: null, // Dictionary by ActorID
  selectedActor: null,
  accessDate: null
}
const getters = {
  accessDate: state => state.accessDate,
  currentLandscape: state => state.landscape,
  currentLandscapeId: state => state.landscape && state.landscape._id,
  selectedActor: state => state.selectedActor,
  selectedActorID: state => {
    if (state.selectedActor) return state.selectedActor._id
  },
  getActors: state => state.actors,
  getActorByID: state => actorID => state.actors[actorID] || {color: 'color0'},
  getUserByID: state => userID =>
    (state.landscape && state.landscape.users[userID]) || {},
  managerList: (state, getters) => {
    return getters.currentLandscapeActiveUsers.filter(user => {
      if (user.role === Roles.MANAGER) {
        if (
          !Array.isArray(user.managedActors) ||
          user.managedActors.length === 0
        ) {
          return true // global
        } else {
          return user.managedActors.includes(getters.selectedActor._id)
        }
      }
    })
  },
  adminList: (state, getters) =>
    getters.currentLandscapeActiveUsers.filter(
      user => user.role === Roles.ADMIN
    ),
  owner: (state, getters) =>
    getters.currentLandscapeActiveUsers
      .filter(user => user.role === Roles.OWNER)
      .pop(),
  getUserRole: (state, getters) => userID => {
    if (state.landscape && state.landscape.users)
      return getters.getUserByID(userID).role
  },
  getRole: state => {
    if (state.landscape && state.landscape.currentUser)
      return state.landscape.currentUser.role
  },
  isOwner: state =>
    state.landscape &&
    state.landscape.currentUser &&
    state.landscape.currentUser.role === Roles.OWNER,
  isAdmin: state =>
    state.landscape &&
    state.landscape.currentUser &&
    state.landscape.currentUser.role === Roles.ADMIN,
  isManager: state =>
    state.landscape &&
    state.landscape.currentUser &&
    state.landscape.currentUser.role === Roles.MANAGER,
  hasAdminRights: (state, getters) => getters.isAdmin || getters.isOwner,
  hasManagerRightsActor: (state, getters) => actor => {
    // if (actor && actor.name === 'BUGS') return false
    if (getters.hasAdminRights) return true
    if (getters.isManager) {
      const currentUser = state.landscape && state.landscape.currentUser
      if (
        !Array.isArray(currentUser.managedActors) ||
        currentUser.managedActors.length === 0
      )
        return true // global manager
      return (
        actor && currentUser && currentUser.managedActors.includes(actor._id)
      )
    }
  },
  hasManagerRights: (state, getters) => {
    return getters.hasManagerRightsActor(getters.selectedActor)
  },
  allowedToFeeds: (state, getters) =>
    getters.hasAdminRights || getters.isManager,
  accessControl: state => prop => {
    if (
      state.landscape &&
      state.landscape.account &&
      state.landscape.account.accessControl
    ) {
      return state.landscape.account.accessControl[prop]
    }
    return false
  },
  allowedToOutputs: (state, getters) =>
    getters.accessControl(AccessControl.OUTPUTS),
  allowedToScores: (state, getters) =>
    getters.accessControl(AccessControl.SCORES),
  allowedToWordcloud: (state, getters) =>
    getters.accessControl(AccessControl.WORDCLOUD),
  hasWatsonKeywords: (state, getters) =>
    getters.accessControl(AccessControl.WATSON_KEYWORDS),
  rootActorLimit: state => state.rootActorLimit,
  // Find landscape validating slugId (last 6 characters)
  findLandscapeBySlug: (state, getters) => slug => {
    const slugId = slug.slice(-6)
    return getters.landscapes.find(
      landscape => slugId === landscape.slug.slice(-6)
    )
  },
  findInvitedLandscapeBySlug: (state, getters) => slug => {
    const slugId = slug.slice(-6)
    return getters.invitedLandscapes.find(
      landscape => slugId === landscape.slug.slice(-6)
    )
  },
  currentLandscapeUsers: (state, getters) => {
    if (getters.currentLandscape)
      return Object.keys(getters.currentLandscape.users).map(
        id => getters.currentLandscape.users[id]
      )
    return []
  },
  currentLandscapeActiveUsers: (state, getters) => {
    if (getters.currentLandscape)
      return Object.keys(getters.currentLandscape.users)
        .map(id => getters.currentLandscape.users[id])
        .filter(user => !user.ex)
    return []
  },
  hasFeedType: state => type => {
    return (
      !RestrictedFeedSources.includes(type) ||
      !!(
        state.landscape &&
        state.landscape.feedSources &&
        state.landscape.feedSources.find(fs => fs.type === type)
      )
    )
  },
  hasFeedTypeInCreatedSources: (state, getters) => type => {
    const sources = getters.landscapeSourcesNoActor || []
    return sources.find(fs => fs.type === type)
  },
  subscribedToPipe: state => pipe => {
    if (state.landscape && state.landscape.feedSources) {
      const pipes = state.landscape.feedSources.find(
        fs => fs.type === FeedSources.PIPE
      )
      return pipes.subscribed[pipe.pipeId][pipe._id]
    }
  },
  totalUsers(state) {
    let users = []
    for (let key in state.landscape.users) {
      const user = state.landscape.users[key]
      if (!user.ex) users.push(user)
    }
    return users.length
  },
  isStartup(state) {
    return state.landscape && !!state.landscape.startup
  },
  allowedSwitchType(state) {
    return state.landscape && !!state.landscape.switchType
  }
}

const mutations = {
  setLandscape(state, landscape) {
    if (state.landscape) {
      state.landscape = null
      state.selectedActor = null
    }
    state.landscape = landscape
  },
  setActors(state, actors) {
    state.actors = actors
  },
  setAccessDate(state, buffer = 0) {
    state.accessDate = Date.now() + buffer
  },
  setStats(state, stats) {
    if (state.selectedActor) Vue.set(state.selectedActor, 'stats', stats)
  },
  setSelectedActor(state, selectedActor) {
    state.selectedActor = selectedActor
  },
  removeLandscape(state) {
    state.landscape = null
    state.selectedActor = null
  },
  updateLandscapeActors(state, actors) {
    const n = Object.assign({}, state.landscape)
    n.actors = actors
    state.landscape = n
  },
  updateManagedActors(state, actorId) {
    const currentUser = state.landscape && state.landscape.currentUser
    if (currentUser && currentUser.managedActors)
      currentUser.managedActors.push(actorId)
  },
  removeActor() {
    // TODO remove selectedActor !?
  },
  setProfile(state, profile) {
    profile.date = new Date().toISOString()
    state.selectedActor.profile = Object.assign(
      {},
      state.selectedActor.profile,
      profile
    )
  },
  updateLandscapeUsers(state, users) {
    state.landscape = Object.assign({}, state.landscape, {users})
  },
  updateLandscape(state, data) {
    if (state.landscape) {
      Vue.set(state.landscape, 'name', data.name)
      Vue.set(state.landscape, 'description', data.description)
      Vue.set(state.landscape, 'slug', data.slug)
    }
  },
  removeUser(state, userId) {
    if (state.landscape) state.landscape.users[userId].ex = true
  },
  injectUser(state, user) {
    if (!state.landscape) return
    if (!state.landscape.users[user._id]) {
      state.landscape.users[user._id] = user
    }
  },
  updateUserRole(state, user) {
    const u = state.landscape.users[user._id]
    u.role = user.role.toUpperCase()
    u.managedActors = user.managedActors
  },
  toggleType(state) {
    state.landscape.startup = !state.landscape.startup
  },
  updateLandscapeTags: (state, tags) => {
    if (!Array.isArray(tags)) return // noop
    const landscapeTags = state.landscape.tags
    tags.forEach(tag => {
      if (landscapeTags.findIndex(t => t.slug === tag.slug) === -1)
        landscapeTags.push(tag)
    })
  },
  putTags: (state, tags) => {
    tags = tags.map(t => {
      if (t.date) {
        const date = new Date(t.date)
        if (date.getTime() > 1) {
          // NOT linux date 1970
          t.date = date
        } else {
          delete t.date
        }
      }
      return t
    })
    Vue.set(state.landscape, 'tags', tags)
  }
}

const actions = {
  loadTags: context => {
    const landscapeId = state.landscape._id
    return api
      .call('get', `${Endpoints.LANDSCAPE}/${landscapeId}/tags`)
      .then(tags => {
        // Testing tags
        /*
        tags = []
        for (let i = 1; i<=1501;i++) {
          let r = (Math.random() + 1).toString(36).substring(7);
          tags.push({label: `${r}-${i}`, slug: `${r}-${i}`})
        }
         */
        // map dates
        context.commit('putTags', tags)
      })
  },
  upsertTag: (context, payload) => {
    const landscapeId = state.landscape._id
    return api
      .call('post', `${Endpoints.LANDSCAPE}/${landscapeId}/tags`, payload)
      .then(tags => {
        context.commit('putTags', tags)
      })
  },
  deleteTags: (context, payload) => {
    const landscapeId = state.landscape._id
    return api
      .call(
        'post',
        `${Endpoints.LANDSCAPE}/${landscapeId}/tags/delete`,
        payload
      )
      .then(tags => {
        context.commit('putTags', tags)
      })
  },
  mergeTags: (context, payload) => {
    const landscapeId = state.landscape._id
    return api
      .call('post', `${Endpoints.LANDSCAPE}/${landscapeId}/tags/merge`, payload)
      .then(tags => {
        context.commit('putTags', tags)
      })
  },
  unloadLandscape: context => {
    context.commit('removeLandscape')
  },
  loadLandscape: (context, id) => {
    return api.call('get', Endpoints.LANDSCAPE + '/' + id).then(landscape => {
      let actorsDictionary = utils.parseActors(
        landscape.actors,
        context.getters.userId
      )
      // Set data
      Vue.$ga.set('landscapeId', landscape._id)
      context.commit('setLandscape', landscape)
      context.commit('setActors', actorsDictionary)
      context.commit('setAccessDate')
      context.commit('setAxes', {axes: landscape.axes, insights: null})
      utils.parseFeedSources(landscape.sources, context.getters)
      context.commit('setMentions', landscape.currentUser.mentions)
      context.commit('setLandscapeSources', landscape.sources)
      context.commit('setGlobalFilters', landscape.globalFilters)
      context.dispatch('setSelectedActor')
      context.dispatch('unloadTopics')
      context.commit('insights/reset')
      context.commit('insights/filters/reset')
      context.commit('insights/settings/reset')
      context.commit('premises/filters/reset')
      context.commit('preferences/reset')
      context.commit('preferences/save', landscape.settings)
      if (landscape.currentUser.preferences) {
        context.commit('preferences/save', landscape.currentUser.preferences)
        context.dispatch(
          'insights/filters/setPreferences',
          landscape.currentUser.preferences
        )
        context.dispatch(
          'premises/filters/setPreferences',
          landscape.currentUser.preferences
        )
      }
      return landscape
    })
  },
  loadLandscapeStats(context, landscapeId) {
    return api.call('get', `landscapes/${landscapeId}/stats`)
  },
  setAccessDate: context => {
    context.commit('setAccessDate')
  },
  createLandscape: (context, payload) => {
    Vue.$ga.event(
      gaComposeEvent(
        GA_EVENTS.LANDSCAPE_CREATE,
        payload.template || 'no template'
      )
    )
    return api.call('post', Endpoints.LANDSCAPE, payload).then(landscape => {
      context.commit('addNewLandscape', landscape)
      return landscape.slug
    })
  },
  editLandscape: (context, payload) => {
    const landscapeId = state.landscape._id
    return api
      .call('put', Endpoints.LANDSCAPE + '/' + landscapeId, payload)
      .then(landscape => {
        context.commit('updateLandscape', landscape)
        context.commit('updateLandscapeInList', landscape)
      })
  },
  deleteLandscape: (context, landscape = state.landscape) => {
    return api.call('delete', `/landscapes/${landscape._id}`).then(() => {
      context.commit('removeLandscapeFromList', landscape._id)
      const msg = `${landscape.name} ${AlertMessages.DELETE_LANDSCAPE}`
      EventHub.emit(Events.SHOW_SNACKBAR, msg)
    })
  },
  createActor: (context, payload) => {
    payload.verb = 'post'
    return context.dispatch('actorCRUD', payload)
  },
  editActor: (context, payload) => {
    payload.verb = 'put'
    return context.dispatch('actorCRUD', payload)
  },
  deleteActor: context => {
    return context.dispatch('actorCRUD', {verb: 'delete'})
  },
  actorCRUD: (context, payload) => {
    const landscapeId = state.landscape._id
    const actorId = state.selectedActor ? state.selectedActor._id : '' // '' === root case
    return api
      .call(
        payload.verb,
        `landscapes/${landscapeId}/actors/${actorId}`,
        payload
      )
      .then(response => getPathArrayFromApiResponse(context, response, true))
  },
  moveActor: (context, fatherId) => {
    const landscapeId = state.landscape._id
    const actorId = state.selectedActor._id
    if (fatherId === landscapeId) fatherId = '' // do not pass value to move into root
    return api
      .call(
        'put',
        `landscapes/${landscapeId}/actors/${actorId}/move/${fatherId}`
      )
      .then(response => getPathArrayFromApiResponse(context, response))
  },
  mergeActor: (context, payload) => {
    const landscapeId = state.landscape._id
    const actorId = state.selectedActor._id
    return api
      .call(
        'post',
        `landscapes/${landscapeId}/actors/${actorId}/merge`,
        payload
      )
      .then(response => getPathArrayFromApiResponse(context, response))
  },
  squashActor: context => {
    const landscapeId = state.landscape._id
    const actorId = state.selectedActor._id
    return api
      .call('post', `landscapes/${landscapeId}/actors/${actorId}/squash`)
      .then(response => getPathArrayFromApiResponse(context, response))
  },
  updateProfile: (context, payload) => {
    const landscapeId = state.landscape._id
    const actorId = state.selectedActor._id
    return api
      .call(
        'patch',
        `landscapes/${landscapeId}/actors/${actorId}/profile`,
        payload
      )
      .then(() => context.commit('setProfile', payload))
  },
  loadActorStats: context => {
    const landscapeId = state.landscape._id
    const actorId = state.selectedActor._id
    return api
      .call('get', `landscapes/${landscapeId}/actors/${actorId}/stats`)
      .then(stats => context.commit('setStats', stats))
  },
  loadAnal: (context, opts) => {
    const landscapeId = context.getters.currentLandscapeId
    opts.actorId = context.getters.selectedActorID
    return api.call('post', `landscapes/${landscapeId}/analysis`, opts)
  },
  setSelectedActor: ({commit, rootState}) => {
    let selectedActor = null
    const actors = rootState.route.params.actor
    if (actors) {
      // From URL => actors = String | From router actors = Array
      let actorsName = Array.isArray(actors) ? actors : actors.split('/')
      // In some cases (router + actor.pathArr) the Array is observable
      // Pass always a clean copy of actorsName
      let selectedActorResults = utils.parseAndMatchActor(
        actorsName.slice(),
        state.landscape.actors
      )
      selectedActor = selectedActorResults.selectedActor
      if (selectedActorResults.is404) {
        const actorName = selectedActor.name || state.landscape.name
        const msg = `Actor '${selectedActorResults.unmatchedActorName}' ${AlertMessages.ACTOR_NOT_FOUND} ${actorName}`
        newAlert(msg, Alert.WARNING, false)
        router.push({
          name: 'info',
          params: {actor: selectedActor.pathArr}
        })
      }
    }
    commit('setSelectedActor', selectedActor)
  },
  loadUsers: context => {
    const landscapeId = state.landscape._id
    return api.call('get', `landscapes/${landscapeId}/users`).then(response => {
      context.commit('updateLandscapeUsers', response.users)
      return response
    })
  },
  landscapeActivity: () => {
    const landscapeId = state.landscape._id
    return api
      .call('get', `landscapes/${landscapeId}/activity`)
      .then(response => {
        return response
      })
  },
  removeUser: (context, {userId, landscapeId = state.landscape._id}) => {
    return api
      .call('delete', `landscapes/${landscapeId}/users/${userId}`)
      .then(() => context.commit('removeUser', userId))
  },
  removeDowngradedUser: (
    context,
    {userId, landscapeId = state.landscape._id}
  ) => {
    return api.call('delete', `landscapes/${landscapeId}/downgraded/${userId}`)
  },
  inviteUsers: (context, emails) => {
    Vue.$ga.event(GA_EVENTS.USER_INVITE)
    const landscapeId = state.landscape._id
    const body = {
      emails: emails
    }
    return api.call('post', `landscapes/${landscapeId}/users/invite`, body)
  },
  reinviteUser: (context, email) => {
    const landscapeId = state.landscape._id
    const body = {email}
    return api.call('post', `landscapes/${landscapeId}/users/reinvite`, body)
  },
  revokeInvitation: (context, email) => {
    const landscapeId = state.landscape._id
    return api.call('delete', `landscapes/${landscapeId}/invitedUsers/${email}`)
  },
  setRole: (context, {role, invitation = false, id, actors = null}) => {
    const landscapeId = state.landscape._id
    const isManager = role.toUpperCase() === Roles.MANAGER
    const body = isManager && actors ? {actors} : null
    const type = invitation ? 'invitedUsers' : 'users'
    const endpoint = `landscapes/${landscapeId}/${type}/${id}/${role}`
    return api.call('post', endpoint, body).then(user => {
      if (!invitation) {
        context.commit('updateUserRole', user)
      }
    })
  },
  toggleLandscapeType({commit}, superadmin = false) {
    const landscapeId = state.landscape._id
    const endpoint = superadmin
      ? `superadmin/landscapes/${landscapeId}/premises/toggle/`
      : `landscapes/${landscapeId}/type/toggle/`
    return api.call('post', endpoint).then(() => {
      commit('toggleType')
      router.push({name: 'info'}).catch(() => {})
    })
  }
}

const getPathArrayFromApiResponse = (context, response, fromCreate = false) => {
  const actorsDictionary = utils.parseActors(response.actors)
  context.commit('updateLandscapeActors', response.actors)
  context.commit('setActors', actorsDictionary)
  context.commit('setAccessDate', 1000)
  if (fromCreate) context.commit('updateManagedActors', response.actorId)
  let pathArray
  if (response.actorId !== '0') {
    // actorId '0' in response means first level actor was deleted
    pathArray = context.getters.getActorByID(response.actorId).pathArr
  }
  return pathArray
}

export default {
  state,
  getters,
  actions,
  mutations,
  modules: {preferences, tags}
}
